/**
 * Public ids for records the portal has already loaded, so request URLs name a
 * record by its public id (`job_…`, `quote_…`, `inv_…`, `appt_…`, `cust_…`)
 * instead of its primary key.
 *
 * The api accepts either id on every route and its payloads carry both. The
 * domain objects (and every equality check in the app) keep the integer id:
 * services record each id/public-id pair here as payloads arrive, and the
 * ENDPOINTS builders send a known record's public id in its place. An id never
 * seen in a payload is sent unchanged, so the integer stays the fallback.
 */
export type RecordId = number | string;

export type PublicIdKind =
  | 'appointment'
  | 'customer'
  | 'invoice'
  | 'job'
  | 'quote';

// A pair is recorded only when the public id carries its model's prefix, so a
// mis-paired field (e.g. a technician's `tech_` id) can never be sent as a
// customer's.
const PREFIX: Record<PublicIdKind, string> = {
  appointment: 'appt_',
  customer: 'cust_',
  invoice: 'inv_',
  job: 'job_',
  quote: 'quote_',
};

const known = new Map<string, string>();

function keyFor(kind: PublicIdKind, id: RecordId): string {
  return `${kind}:${String(id)}`;
}

export function rememberPublicId(
  kind: PublicIdKind,
  id: unknown,
  publicId: unknown,
): void {
  if (typeof id !== 'number' && typeof id !== 'string') return;
  if (typeof publicId !== 'string' || !publicId.startsWith(PREFIX[kind])) {
    return;
  }
  known.set(keyFor(kind, id), publicId);
}

/** Record `record[idKey]` / `record.public_id` from an untyped payload object. */
export function rememberRecord(
  kind: PublicIdKind,
  record: unknown,
  idKey = 'id',
): void {
  if (!record || typeof record !== 'object') return;
  const fields = record as Record<string, unknown>;
  rememberPublicId(kind, fields[idKey], fields.public_id);
}

/** A `v2/customers/_customer_short` block: `customer_id` + `customer_public_id`. */
export function rememberCustomerShort(customer: unknown): void {
  if (!customer || typeof customer !== 'object') return;
  const fields = customer as Record<string, unknown>;
  rememberPublicId('customer', fields.customer_id, fields.customer_public_id);
}

function fieldsOf(payload: unknown): Record<string, unknown> {
  return payload && typeof payload === 'object'
    ? (payload as Record<string, unknown>)
    : {};
}

function eachOf(list: unknown, record: (item: unknown) => void): void {
  if (Array.isArray(list)) list.forEach(record);
}

/** `GET /v1/appointments/:id` (v1/appointments/_appointment). */
export function rememberAppointmentPayload(payload: unknown): void {
  rememberRecord('appointment', payload);
}

/** `GET /v2/jobs/:id` (v2/jobs/_job): the job and the records nested in it. */
export function rememberJobPayload(payload: unknown): void {
  const job = fieldsOf(payload);
  rememberRecord('job', job);
  rememberRecord('customer', job.customer);
  if (job.invoice) rememberInvoicePayload(job.invoice);
  eachOf(job.quotes, rememberQuotePayload);
  eachOf(job.appointments, (appointment) =>
    rememberRecord('appointment', appointment),
  );
}

/** `GET /v2/invoices/:id` (v2/invoices/_invoice): keyed by `invoice_id`. */
export function rememberInvoicePayload(payload: unknown): void {
  const invoice = fieldsOf(payload);
  rememberRecord('invoice', invoice, 'invoice_id');
  rememberPublicId('job', invoice.job_id, invoice.job_public_id);
  rememberCustomerShort(invoice.customer);
  rememberCustomerShort(invoice.billing_customer);
}

/** `GET /v2/customers/:customer_id/quotes/:id` (v2/quotes/_quote). */
export function rememberQuotePayload(payload: unknown): void {
  const quote = fieldsOf(payload);
  rememberRecord('quote', quote);
  rememberPublicId('job', quote.job_id, quote.job_public_id);
  rememberCustomerShort(quote.customer);
  rememberCustomerShort(quote.billing_customer);
}

/** `POST /v2/pay/:token/exchange` (v2/payment_links/exchange). */
export function rememberPayExchangePayload(payload: unknown): void {
  rememberRecord('invoice', fieldsOf(payload).invoice);
}

/** The id to put in a URL: the record's public id when known, else `id`. */
export function urlId(kind: PublicIdKind, id: RecordId): RecordId {
  return known.get(keyFor(kind, id)) ?? id;
}

/** Test seam: forget every recorded pair. */
export function clearPublicIds(): void {
  known.clear();
}
